import {
  CreateQualificationListDTO,
  QualificationJSON,
  QualificationListJSON,
  QualificationListType,
  QualificationType,
} from '@scale/shared/types/qualification/types';
import { defaultMandatoryName } from './constants';
import { create } from 'zustand';
import { WorkerSkillResponse } from '@scale/genai-data-service-client';
import _ from 'lodash';

export interface QualificationWithAlternates extends QualificationJSON {
  altDimensions?: QualificationJSON[];
}

export type QualificationList = Omit<
  QualificationListJSON | CreateQualificationListDTO,
  'qualifications'
> & {
  qualifications: QualificationWithAlternates[];
  type?: QualificationListType;
  listIndex?: number;
  experimentalGroup?: string;
};

function isQualification(o: any): o is QualificationJSON {
  return (
    typeof o?._id === 'string' &&
    typeof o?.qualificationType === 'string' &&
    typeof o?.displayName === 'string'
  );
}

function isQualificationList(o: any): o is QualificationList {
  return (
    Array.isArray(o?.qualifications) &&
    typeof o?.name === 'string' &&
    o?.qualifications.every((q: any) => isQualification(q))
  );
}

// Define the state
interface QualificationsStoreProps {
  mandatory: CreateQualificationListDTO | null;
  lists: QualificationList[];
  violationLists: QualificationListJSON[];
  isDirty: boolean;
  initLists: QualificationListJSON[];
  jsonError?: string;
  allQualifications: QualificationJSON[];
  allWorkerSkills: WorkerSkillResponse[];
  hasWarnings: boolean;
  experimentalList: CreateQualificationListDTO[] | null;
  isExperimentBackfillEnabled?: boolean;
}

// Define the actions that can be taken on the state
interface QualificationsStoreActions {
  init: (initLists: QualificationListJSON[], isExperimentBackfillEnabled?: boolean) => void;
  addList: (list: CreateQualificationListDTO, isExperimental?: boolean) => void;
  cloneList: (list: QualificationList) => void;
  setLists: (lists: QualificationList[]) => void;
  removeList: (index: number, experimentalGroup?: string) => void;
  moveList: (fromIndex: number, toIndex: number) => void;
  addQualification: (
    listIndex: number,
    qualification: QualificationWithAlternates,
    experimentalGroup?: string,
  ) => void;
  removeQualification: (
    listIndex: number,
    qualificationIndex: number,
    experimentalGroup?: string,
  ) => void;
  editQualification: (
    listIndex: number,
    qualificationIndex: number,
    qualification: QualificationWithAlternates,
  ) => void;
  moveQualification: (listIndex: number, qualificationIndex: number, toIndex: number) => void;
  editListName: (index: number, name: string) => void;
  makeMandatory: (index: number) => void;
  makeOptional: () => void;
  resetState: () => void;
  jsonString: () => string;
  setJsonString: (json: string) => void;
  setAllQualifications: (quals: QualificationJSON[]) => void;
  setAllWorkerSkills: (skills: WorkerSkillResponse[]) => void;
  setHasWarnings: (hasWarnings: boolean) => void;
}

type QualificationsStore = QualificationsStoreProps & QualificationsStoreActions;

// Create the store
export const useQualificationsStore = create<QualificationsStore>()((set, get) => {
  return {
    // Initial state
    mandatory: null,
    lists: [],
    experimentalList: null,
    violationLists: [],
    isDirty: false,
    initLists: [],
    allQualifications: [],
    allWorkerSkills: [],
    hasWarnings: false,
    isExperimentBackfillEnabled: false,
    // Actions
    init: (initLists, isExperimentBackfillEnabled) => {
      set(() => getInitialListState(initLists, isExperimentBackfillEnabled));
    },
    addList: (list, isExperimental = false) =>
      set(state => ({
        lists: isExperimental ? state.lists : [...state.lists, list],
        experimentalList: isExperimental
          ? [...(state.experimentalList || []), list]
          : state.experimentalList,
        isDirty: true,
      })),
    setLists: lists =>
      set(state => {
        // do not import violations lists from an existing project
        const nonViolationLists = lists.filter(l => l.type !== QualificationListType.Violation);
        const [mandatoryList, oneOfListsFromMandatory] = factorLists(
          nonViolationLists,
          state.isExperimentBackfillEnabled,
        );
        const [experimentalList, onOfLists] = factorExperimentalLists(
          oneOfListsFromMandatory,
          state.isExperimentBackfillEnabled,
        );
        return {
          ...state,
          mandatory: mandatoryList,
          lists: onOfLists,
          isDirty: true,
          experimentalList,
        };
      }),
    cloneList: list =>
      set(state => ({
        lists: [
          ...state.lists,
          {
            name: `Copy of ${list.name}`,
            qualifications: _.cloneDeep(list.qualifications),
          },
        ],
        isDirty: true,
      })),
    removeList: (index, experimentalGroup = undefined) =>
      set(state => removeList(state, index, experimentalGroup)),
    moveList: (fromIndex, toIndex) =>
      set(state => ({
        lists: Array.from(state.lists).map((list, idx) => {
          const items = Array.from(state.lists);
          const [reorderedItem] = items.splice(fromIndex, 1);
          items.splice(toIndex, 0, reorderedItem);
          return items[idx];
        }),
        isDirty: true,
      })),
    addQualification: (listIndex, qualification, experimentalGroup = undefined) => {
      const addQual = (list: QualificationList, isExperimental = false) => ({
        ...list,
        qualifications: [...list.qualifications, qualification],
        experimentalGroup: isExperimental
          ? qualification.metadata?.experimentGroupName || list.experimentalGroup
          : list.experimentalGroup,
      });
      set(state => ({
        mandatory: listIndex === -1 && state.mandatory ? addQual(state.mandatory) : state.mandatory,
        lists: !experimentalGroup
          ? state.lists.map((list, i) => (i === listIndex ? addQual(list) : list))
          : state.lists,
        experimentalList: experimentalGroup
          ? state.experimentalList?.map((list, i) => (i === listIndex ? addQual(list, true) : list))
          : state.experimentalList,
        isDirty: true,
      }));
    },
    removeQualification: (listIndex, qualificationIndex, experimentalGroup = undefined) => {
      const removeQual = (list: QualificationList) => ({
        ...list,
        qualifications: list.qualifications.filter((_, i) => i !== qualificationIndex),
      });
      set(state => ({
        mandatory:
          listIndex === -1 && state.mandatory ? removeQual(state.mandatory) : state.mandatory,
        lists: !experimentalGroup
          ? state.lists.map((list, i) => (i === listIndex ? removeQual(list) : list))
          : state.lists,
        experimentalList: experimentalGroup
          ? state.experimentalList?.map((list, i) => (i === listIndex ? removeQual(list) : list))
          : state.experimentalList,
        isDirty: true,
      }));
    },
    editQualification: (listIndex, qualificationIndex, qualification) => {
      const editQual = (list: QualificationList) => ({
        ...list,
        qualifications: list.qualifications.map((q, i) =>
          i === qualificationIndex ? qualification : q,
        ),
      });
      set(state => ({
        mandatory:
          listIndex === -1 && state.mandatory ? editQual(state.mandatory) : state.mandatory,
        lists: state.lists.map((list, i) => (i === listIndex ? editQual(list) : list)),
        isDirty: true,
      }));
    },
    moveQualification: (listIndex, qualificationIndex, toIndex) => {
      const moveQual = (list: QualificationList) => {
        const newQuals = [...list.qualifications];
        const item = newQuals.splice(qualificationIndex, 1)[0];
        newQuals.splice(toIndex, 0, item);
        return { ...list, qualifications: newQuals };
      };
      set(state => ({
        mandatory:
          listIndex === -1 && state.mandatory ? moveQual(state.mandatory) : state.mandatory,
        lists: state.lists.map((list, i) => (i === listIndex ? moveQual(list) : list)),
        isDirty: true,
      }));
    },
    editListName: (listIndex, name) =>
      set(state => ({
        mandatory:
          listIndex === -1 && state.mandatory ? { ...state.mandatory, name } : state.mandatory,
        lists: state.lists.map((list, i) => (i === listIndex ? { ...list, name } : list)),
        isDirty: true,
      })),
    makeMandatory: index =>
      set(state => {
        if (state.lists.length <= 2) {
          // If there are only two or fewer optional lists, none can be mandatory
          return {};
        }
        return {
          mandatory: _.isNil(state.mandatory)
            ? state.lists[index]
            : {
                ...state.mandatory,
                qualifications: [
                  ...state.mandatory.qualifications,
                  ...state.lists[index].qualifications,
                ],
              },
          lists: state.lists.filter((_, i) => i !== index),
          isDirty: true,
        };
      }),
    makeOptional: () =>
      set(state => {
        if (!state.mandatory) {
          return {};
        }
        // Only rename the list if it's using the default mandatory name
        const newList = {
          name:
            state.mandatory.name === defaultMandatoryName
              ? `Qualification List ${state.lists.length + 1}`
              : state.mandatory.name,
          qualifications: state.mandatory.qualifications,
        };
        return {
          lists: [newList, ...state.lists],
          isDirty: true,
          mandatory: null,
        };
      }),
    resetState: () =>
      set(state => getInitialListState([...state.initLists, ...state.violationLists])),
    jsonString: () => JSON.stringify({ mandatory: get().mandatory, lists: get().lists }, null, 2),
    setJsonString: json =>
      set(state => {
        if (json === state.jsonString()) {
          return {
            jsonError: undefined,
          };
        }
        try {
          const validatedState = validateJsonString(json);
          return {
            mandatory: validatedState.mandatory,
            lists: validatedState.lists,
            isDirty: true,
            jsonError: undefined,
          };
        } catch (e: any) {
          return {
            jsonError: e.message || JSON.stringify(e),
          };
        }
      }),
    setAllQualifications: quals => set({ allQualifications: quals }),
    setAllWorkerSkills: skills => set({ allWorkerSkills: skills }),
    setHasWarnings: hasWarnings => set({ hasWarnings }),
  };
});

/**
 * Get the initial state of the qual list builder from existing project lists.
 * @param qualificationLists The lists to initialize the state with
 * @returns Initialized list builder state with mandatory qualifications factored out into a separate list
 */
function getInitialListState(
  qualificationLists: QualificationListJSON[],
  isExperimentBackfillEnabled?: boolean,
): Pick<
  QualificationsStoreProps,
  'mandatory' | 'lists' | 'experimentalList' | 'isDirty' | 'initLists' | 'violationLists'
> {
  const initLists = qualificationLists.filter(l => l.type !== QualificationListType.Violation);
  const violationLists = qualificationLists.filter(l => l.type === QualificationListType.Violation);
  const [mandatoryList, oneOfListsFromMandatory] = factorLists(
    initLists,
    isExperimentBackfillEnabled,
  );
  const [experimentalList, oneOfLists] = factorExperimentalLists(
    oneOfListsFromMandatory,
    isExperimentBackfillEnabled,
  );
  return {
    mandatory: mandatoryList,
    lists: oneOfLists,
    experimentalList,
    isDirty: false,
    initLists,
    violationLists,
  };
}

function removeList(
  state: QualificationsStore,
  index: number,
  experimentalGroup?: string,
): Partial<QualificationsStore> {
  let updates: Partial<QualificationsStoreProps> & Pick<QualificationsStoreProps, 'lists'>;
  if (experimentalGroup) {
    updates = {
      experimentalList: state.experimentalList?.filter((_, i) => i !== index),
      lists: state.lists,
      isDirty: true,
    };
  } else {
    updates = {
      lists: state.lists.filter((_, i) => i !== index),
      isDirty: true,
    };
  }
  if (index === -1) {
    updates.mandatory = null;
  }
  if (state.mandatory && updates.lists.length < 2) {
    // Need to convert mandatory to regular list here
    updates.mandatory = null;
    const newList = {
      name:
        state.mandatory.name === defaultMandatoryName
          ? `Qualification List ${updates.lists.length + 1}`
          : state.mandatory.name,
      qualifications: state.mandatory.qualifications,
    };
    updates.lists = [newList, ...updates.lists];
  }
  return updates;
}

/**
 * Check that the json string contains valid 'mandatory' and 'lists' fields
 * before storing to state.
 * @param jsonString String to parse into state properties
 * @returns Validated lists and mandatory state props
 */
function validateJsonString(
  jsonString: string,
): Pick<QualificationsStoreProps, 'lists' | 'mandatory'> {
  const jsonObj = JSON.parse(jsonString);
  if (!('lists' in jsonObj) || !Array.isArray(jsonObj.lists)) {
    throw new Error('lists is required.');
  }
  if ('mandatory' in jsonObj && !isQualificationList(jsonObj.mandatory)) {
    throw new Error('Invalid mandatory list.');
  }
  const invalidListIndex = jsonObj.lists.findIndex((l: Object) => !isQualificationList(l));
  if (invalidListIndex > 0) {
    throw new Error(`Invalid list at position ${invalidListIndex}`);
  }
  return {
    mandatory: 'mandatory' in jsonObj ? jsonObj.mandatory : null,
    lists: jsonObj.lists,
  };
}

/**
 * Qualifications list are displayed differently in the frontend than they are saved in the backend.
 * Frontend: Mandatory = [A, B, C], Optional = [[D, E], [F, G]]
 * Backend: Lists = [[A, B, C, D, E], [A, B, C, F, G]]
 * In the backend, we evaluate the qualification list for a project by "and"-ing together quals within a list
 * and "or"-ing each list. In the frontend, we display lists as either mandatory or optional/oneof.
 * Therefore, we need to factor out the items that are common to every list to include them in the mandatory list
 * instead of each of the optional lists when we display them in the frontend.
 */
function factorLists(
  qualLists: QualificationList[],
  isExperimentBackfillEnabled?: boolean,
): [CreateQualificationListDTO | null, QualificationList[]] {
  if (qualLists.length < 2) {
    return [null, qualLists];
  }
  // Find qualifications that are common to all lists
  const commonQualifications = _.intersectionBy(
    ...qualLists.map(list => list.qualifications),
    '_id',
  );

  // Return early if no common quals or if any common qual is experimental
  if (
    commonQualifications.length === 0 ||
    (commonQualifications.some(q => q.qualificationType === QualificationType.Experiment) &&
      isExperimentBackfillEnabled)
  ) {
    return [null, qualLists];
  }

  // Remove these common quals from each list
  const factoredLists = qualLists
    .map(list => {
      return {
        ...list,
        qualifications: _.differenceBy(list.qualifications, commonQualifications, '_id'),
      };
    })
    .filter(list => list.qualifications.length > 0);

  if (factoredLists.length < 2) {
    return [null, qualLists];
  }

  return [{ name: defaultMandatoryName, qualifications: commonQualifications }, factoredLists];
}

function factorExperimentalLists(
  qualLists: QualificationList[],
  isExperimentBackfillEnabled?: boolean,
): [CreateQualificationListDTO[] | null, QualificationList[]] {
  if (!isExperimentBackfillEnabled) {
    return [null, qualLists];
  }
  // First, find all experimental qualifications
  const experimentalQuals = _.uniqBy(
    qualLists.flatMap(l =>
      l.qualifications.filter(q => q.qualificationType === QualificationType.Experiment),
    ),
    '_id',
  );

  if (!experimentalQuals || experimentalQuals.length === 0) {
    return [null, qualLists];
  }

  // For each experimental qualification, create a separate list
  const experimentalLists = experimentalQuals.map(expQual => {
    const listsWithExp = qualLists.filter(l => l.qualifications.some(q => q._id === expQual._id));

    // Find common non-experimental qualifications among these lists
    const commonQuals = _.intersectionBy(
      ...listsWithExp.map(list =>
        list.qualifications.filter(q => q.qualificationType !== QualificationType.Experiment),
      ),
      '_id',
    );

    return {
      experimental: expQual,
      commonQualifications: commonQuals,
    };
  });

  // Combine all experimental groups into one list
  const experimentalList = experimentalLists.map(l => {
    return {
      name: l.experimental.metadata?.experimentGroupName || 'Experimental Qualifications',
      qualifications: [l.experimental, ...l.commonQualifications],
      experimentalGroup: l.experimental.metadata?.experimentGroupName,
    };
  });

  // Get all qualification IDs that are part of experimental lists
  const experimentalListQualIds = new Set(
    experimentalList.flatMap(l => l.qualifications.map(q => q._id)),
  );
  const experimentQualIds = new Set(
    experimentalList.flatMap(l =>
      l.qualifications
        .filter(q => q.qualificationType === QualificationType.Experiment)
        .map(q => q._id),
    ),
  );

  // Remove lists that contain experimental qualifications and filter out qualifications that are in experimental lists
  const remainingLists = qualLists
    .map(l => ({
      ...l,
      experimentalGroup: l.qualifications.find(q => experimentQualIds.has(q._id))?.metadata
        ?.experimentGroupName,
      qualifications: l.qualifications.filter(q => !experimentalListQualIds.has(q._id)),
    }))
    .filter(l => l.qualifications.length > 0); // Remove any lists that become empty

  return [experimentalList, remainingLists];
}

/**
 * Unfactor lists by distributing the mandatory qualifications back into each list
 * @param mandatory The mandatory list to add to each list
 * @param lists The lists to distribute
 * @returns The distributed lists
 */
export function distributeLists(
  mandatory: CreateQualificationListDTO | null,
  lists: QualificationList[],
  experimentalList?: CreateQualificationListDTO[] | null,
): QualificationList[] {
  if (_.isNil(mandatory) && _.isNil(experimentalList)) {
    return lists.map((list, index) => ({
      ...list,
      listIndex: index,
    }));
  }

  if (_.isNil(experimentalList)) {
    return lists.map((qualList, index) => {
      const copiedList = _.cloneDeep(qualList);
      copiedList.qualifications = _.uniqBy(
        [...mandatory!.qualifications, ...copiedList.qualifications],
        '_id',
      );
      copiedList.listIndex = index;
      return copiedList;
    });
  }

  const mandatoryQuals = mandatory?.qualifications || [];
  const copiedLists = [];
  const seenQualSets = new Set();

  let i = 0;
  for (const qualList of lists) {
    const matchingExpList = experimentalList || [];
    for (const expList of matchingExpList) {
      // Create a sorted string of qualification IDs to use as a unique key
      const combinedQuals = _.uniqBy(
        [...mandatoryQuals, ...qualList.qualifications, ...(expList?.qualifications || [])],
        '_id',
      );
      const qualSetKey = combinedQuals
        .map(q => q._id)
        .sort()
        .join(',');

      // Skip if we've already created a list with these exact qualifications
      if (seenQualSets.has(qualSetKey)) {
        continue;
      }
      seenQualSets.add(qualSetKey);

      const copiedList = _.cloneDeep(qualList);
      copiedList.qualifications = combinedQuals;
      copiedList.listIndex = i;
      copiedList.experimentalGroup = expList.experimentalGroup;
      // @ts-ignore
      delete copiedList._id;
      copiedLists.push(copiedList);
      i++;
    }
  }

  return copiedLists;
}
